import type React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export interface LineSeries {
  key: string;
  name: string;
  color: string;
}

interface LineChartWidgetProps {
  data: Array<Record<string, unknown>>;
  xKey: string;
  lines: LineSeries[];
  height?: number;
  yFormatter?: (value: number) => string;
}

export function LineChartWidget({
  data,
  xKey,
  lines,
  height = 280,
  yFormatter,
}: LineChartWidgetProps) {
  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center text-sm text-muted-foreground"
        style={{ height }}
      >
        No data available for selected filters
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(210,16%,90%)" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "hsl(215,16%,40%)" }}
          tickLine={false}
          axisLine={{ stroke: "hsl(210,16%,85%)" }}
          tickFormatter={(v) => (yFormatter ? yFormatter(v) : String(v))}
        />
        <Tooltip
          contentStyle={{
            border: "1px solid hsl(210,16%,85%)",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(value) => {
            if (typeof value === "number" && yFormatter) return yFormatter(value);
            return value as React.ReactNode;
          }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map((l) => (
          <Line
            key={l.key}
            type="monotone"
            dataKey={l.key}
            name={l.name}
            stroke={l.color}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
